import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { ShieldCheck, RefreshCw, Download, AlertTriangle } from 'lucide-react';
import { api } from '../context/AuthContext';

export const BackupIcon = ShieldCheck;

const CAT = {
  SAFE_DELETE: { label: 'Safe to delete', cls: 'bg-rose-50 text-rose-700 border-rose-200', hint: 'Older than the latest snapshot of the same volume, or an unmanaged copy. No AMI depends on it.' },
  NEEDS_AMI_DEREG: { label: 'Deregister AMI first', cls: 'bg-amber-50 text-amber-700 border-amber-200', hint: 'Belongs to an older AMI that no instance uses. Deregister the AMI, then delete the snapshot.' },
  REVIEW: { label: 'Review', cls: 'bg-violet-50 text-violet-700 border-violet-200', hint: 'Last snapshot of a volume that no longer exists. Delete only if the server is decommissioned.' },
  DLM_MANAGED: { label: 'Managed by DLM', cls: 'bg-sky-50 text-sky-700 border-sky-200', hint: 'Rotated automatically by an active lifecycle policy. Change the policy retention instead.' },
  KEEP: { label: 'Keep', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', hint: 'Latest snapshot of an existing volume, or part of an AMI that is in use / the latest of its group.' },
};
const CAT_ORDER = ['SAFE_DELETE', 'NEEDS_AMI_DEREG', 'REVIEW', 'DLM_MANAGED', 'KEEP'];

const COV = {
  UNPROTECTED: { label: 'Unprotected', cls: 'bg-rose-50 text-rose-700 border-rose-200' },
  PARTIAL: { label: 'Partial', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  PROTECTED: { label: 'Protected', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
};

const AMI_ST = {
  IN_USE: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  KEEP_LATEST: 'bg-sky-50 text-sky-700 border-sky-200',
  DEREGISTER_CANDIDATE: 'bg-amber-50 text-amber-700 border-amber-200',
};

const fmtGiB = (n) => `${Number(n || 0).toLocaleString('en-US')} GiB`;
const Badge = ({ cls, children, title }) => (
  <span title={title} className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] font-medium whitespace-nowrap ${cls}`}>{children}</span>
);

function toCsv(rows) {
  const cols = ['category', 'region', 'snapshotId', 'volumeId', 'sizeGiB', 'started', 'amiId', 'policyId', 'volumeExists', 'isLatest', 'attachedTo', 'reason', 'description'];
  const esc = (v) => {
    const s = v === null || v === undefined ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return [cols.join(',')].concat(rows.map((r) => cols.map((c) => esc(r[c])).join(','))).join('\n');
}

export default function AwsBackupReview() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [view, setView] = useState('recs');
  const [cat, setCat] = useState('SAFE_DELETE');
  const [q, setQ] = useState('');

  const load = useCallback((force) => {
    setLoading(true); setError('');
    api.get('/api/aws-inventory/backup-review', { params: force ? { force: 1 } : {} })
      .then((r) => setData(r.data))
      .catch((e) => setError((e.response && e.response.data && e.response.data.error) || e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(false); }, [load]);

  const snaps = useMemo(() => {
    if (!data) return [];
    const s = q.trim().toLowerCase();
    return data.snapshots
      .filter((r) => r.category === cat)
      .filter((r) => !s || [r.snapshotId, r.volumeId, r.description, r.name, r.amiId, r.region].join(' ').toLowerCase().includes(s))
      .sort((a, b) => a.region.localeCompare(b.region) || a.started.localeCompare(b.started));
  }, [data, cat, q]);

  const exportCsv = () => {
    if (!data) return;
    const rows = data.snapshots.filter((r) => r.category !== 'KEEP' && r.category !== 'DLM_MANAGED');
    const blob = new Blob([toCsv(rows)], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `snapshot-housekeeping-${(data.account && data.account.key) || 'aws'}-${data.generatedAt.slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  if (!data && loading) {
    return <div className="p-8 text-sm text-slate-500 flex items-center gap-2"><RefreshCw className="animate-spin" style={{ width: 16, height: 16 }} /> Analysing snapshots, AMIs and lifecycle policies…</div>;
  }
  if (!data) {
    return (
      <div className="p-6">
        <div className="text-sm text-rose-600 mb-3">{error || 'No data.'}</div>
        <button onClick={() => load(true)} className="px-3 py-1.5 text-sm rounded-lg bg-brand-600 text-white">Retry</button>
      </div>
    );
  }

  const sum = data.summary || {};
  const tabBtn = (key, label) => (
    <button key={key} onClick={() => setView(key)}
      className={`px-3 py-1.5 text-sm rounded-lg ${view === key ? 'bg-brand-600 text-white' : 'text-slate-600 hover:bg-slate-100'}`}>{label}</button>
  );

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold text-slate-800 flex items-center gap-2"><ShieldCheck style={{ width: 18, height: 18 }} className="text-brand-600" /> Backup & Snapshot Review</h2>
            <p className="text-xs text-slate-500 mt-1">
              Policy: keep only the latest snapshot per volume. Read-only analysis — nothing is deleted from here.
              {' '}Regions: {(data.regions || []).join(', ')} · Generated {new Date(data.generatedAt).toLocaleString()}{data.cached ? ' (cached)' : ''}
            </p>
          </div>
          <div className="flex gap-2">
            <button onClick={exportCsv} className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50">
              <Download style={{ width: 14, height: 14 }} /> Export CSV
            </button>
            <button onClick={() => load(true)} disabled={loading} className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-brand-600 text-white disabled:opacity-60">
              <RefreshCw className={loading ? 'animate-spin' : ''} style={{ width: 14, height: 14 }} /> Re-analyse
            </button>
          </div>
        </div>

        {(data.dlmErrors || []).length > 0 && (
          <div className="mt-3 text-xs rounded-lg border border-amber-200 bg-amber-50 text-amber-800 p-2.5 flex gap-2">
            <AlertTriangle style={{ width: 14, height: 14 }} className="shrink-0 mt-0.5" />
            <div>
              Lifecycle Manager could not be read for {data.dlmErrors.map((d) => d.region).join(', ')}: {data.dlmErrors[0].error}
              {' '}Snapshots from DLM policies will be treated as unmanaged until this is fixed.
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-4">
          {CAT_ORDER.map((k) => (
            <button key={k} onClick={() => { setView('snapshots'); setCat(k); }} title={CAT[k].hint}
              className={`text-left rounded-lg border p-3 hover:shadow-sm ${CAT[k].cls}`}>
              <div className="text-[11px] font-medium uppercase tracking-wide opacity-80">{CAT[k].label}</div>
              <div className="text-xl font-semibold">{(sum[k] && sum[k].count) || 0}</div>
              <div className="text-[11px] opacity-80">{fmtGiB(sum[k] && sum[k].sizeGiB)} volume size</div>
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2 mt-3 text-xs text-slate-600">
          <span>EC2 backup coverage:</span>
          {['UNPROTECTED', 'PARTIAL', 'PROTECTED'].map((k) => (
            <Badge key={k} cls={COV[k].cls}>{COV[k].label}: {(data.coverage && data.coverage[k]) || 0}</Badge>
          ))}
          <span className="text-slate-400">· Snapshot size shown is the source volume size; billed size is incremental and usually smaller.</span>
        </div>
      </div>

      <div className="flex gap-1">
        {tabBtn('recs', `Recommendations (${(data.recommendations || []).length})`)}
        {tabBtn('coverage', 'EC2 coverage')}
        {tabBtn('snapshots', 'Snapshots')}
        {tabBtn('amis', 'AMIs')}
        {tabBtn('policies', 'Lifecycle policies')}
      </div>

      {view === 'recs' && (
        <div className="space-y-2">
          {(data.recommendations || []).length === 0 && <div className="bg-white rounded-xl border border-slate-200 p-6 text-center text-sm text-emerald-600">All instances are covered by an active backup policy.</div>}
          {(data.recommendations || []).map((r, idx) => (
            <div key={idx} className="bg-white rounded-xl border border-slate-200 p-3">
              <div className="flex flex-wrap items-center gap-2">
                <Badge cls={r.priority === 'HIGH' ? 'bg-rose-50 text-rose-700 border-rose-200' : r.priority === 'MEDIUM' ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-slate-100 text-slate-600 border-slate-200'}>{r.priority}</Badge>
                <span className="font-medium text-slate-800 text-sm">{r.name}</span>
                {r.instanceId && <span className="text-xs text-slate-400 font-mono">{r.instanceId}</span>}
                {COV[r.status] && <Badge cls={COV[r.status].cls}>{COV[r.status].label}</Badge>}
                {r.unprotectedVolumes > 0 && <span className="text-xs text-slate-500">{r.unprotectedVolumes} unprotected volume(s) · {fmtGiB(r.unprotectedGiB)} · last snapshot {r.lastSnapshot}</span>}
              </div>
              <ul className="mt-2 ml-5 list-disc text-xs text-slate-600 space-y-0.5">
                {r.actions.map((a, i) => <li key={i}>{a}</li>)}
              </ul>
            </div>
          ))}
        </div>
      )}

      {view === 'coverage' && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
          {(data.tagWarnings || []).length > 0 && (
            <div className="m-3 text-xs rounded-lg border border-amber-200 bg-amber-50 text-amber-800 p-2.5">
              Backup tags with no active policy: {data.tagWarnings.map((w) => `${w.tag} (${w.volumes} vol, ${w.region})`).join(' · ')}
            </div>
          )}
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
              <tr><th className="text-left p-2.5">Instance</th><th className="text-left p-2.5">Status</th><th className="text-left p-2.5">Volumes (policy / last snapshot)</th><th className="text-left p-2.5">AMI policy</th></tr>
            </thead>
            <tbody>
              {data.instances.map((i) => (
                <tr key={i.instanceId} className="border-t border-slate-100 align-top">
                  <td className="p-2.5">
                    <div className="font-medium text-slate-800">{i.name || '(no name)'}</div>
                    <div className="text-xs text-slate-500">{i.instanceId} · {i.type} · {i.state} · {i.region}</div>
                  </td>
                  <td className="p-2.5"><Badge cls={COV[i.status].cls}>{COV[i.status].label}</Badge></td>
                  <td className="p-2.5">
                    {i.volumes.map((v) => (
                      <div key={v.volumeId} className="text-xs flex flex-wrap gap-1.5 items-center py-0.5">
                        <span className={v.protected ? 'text-emerald-600' : 'text-rose-600'}>{v.protected ? '●' : '○'}</span>
                        <span className="font-mono text-slate-600">{v.volumeId}</span>
                        <span className="text-slate-500">{v.device} · {v.sizeGiB} GiB</span>
                        {v.tags.map((t) => <span key={t} className="px-1.5 rounded bg-slate-100 text-slate-600">{t}</span>)}
                        <span className="text-slate-500">{v.policies.length ? v.policies.join(', ') : 'no policy'} · last {v.lastSnapshot || 'never'}</span>
                      </div>
                    ))}
                  </td>
                  <td className="p-2.5 text-xs text-slate-600">{i.imagePolicies.length ? i.imagePolicies.join(', ') : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {view === 'snapshots' && (
        <div className="bg-white rounded-xl border border-slate-200">
          <div className="flex flex-wrap gap-2 items-center p-3 border-b border-slate-100">
            {CAT_ORDER.map((k) => (
              <button key={k} onClick={() => setCat(k)}
                className={`px-2.5 py-1 rounded-full border text-xs ${cat === k ? CAT[k].cls + ' font-semibold' : 'border-slate-200 text-slate-600'}`}>
                {CAT[k].label} ({(sum[k] && sum[k].count) || 0})
              </button>
            ))}
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter by ID, volume, description…"
              className="ml-auto text-sm border border-slate-200 rounded-lg px-2.5 py-1 w-64 max-w-full" />
          </div>
          <div className="px-3 pt-2 text-xs text-slate-500">{CAT[cat].hint}</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-slate-500 text-xs uppercase">
                <tr><th className="text-left p-2.5">Snapshot</th><th className="text-left p-2.5">Volume</th><th className="text-right p-2.5">Size</th><th className="text-left p-2.5">Created</th><th className="text-left p-2.5">Reason</th><th className="text-left p-2.5">Description</th></tr>
              </thead>
              <tbody>
                {snaps.map((r) => (
                  <tr key={r.region + r.snapshotId} className="border-t border-slate-100 align-top">
                    <td className="p-2.5"><div className="font-mono text-xs text-slate-700">{r.snapshotId}</div><div className="text-[11px] text-slate-400">{r.region}{r.tier !== 'standard' ? ' · ' + r.tier : ''}</div></td>
                    <td className="p-2.5 text-xs"><span className="font-mono text-slate-600">{r.volumeId}</span>{!r.volumeExists && r.volumeId !== 'vol-ffffffff' && <span className="ml-1 text-rose-500">(deleted)</span>}</td>
                    <td className="p-2.5 text-right text-xs">{r.sizeGiB}</td>
                    <td className="p-2.5 text-xs whitespace-nowrap">{r.started}</td>
                    <td className="p-2.5 text-xs text-slate-600">{r.reason}</td>
                    <td className="p-2.5 text-xs text-slate-500 max-w-md break-words">{r.name ? <b className="text-slate-600">{r.name} </b> : null}{r.description}</td>
                  </tr>
                ))}
                {snaps.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-sm text-slate-400">No snapshots in this category.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {view === 'amis' && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
              <tr><th className="text-left p-2.5">AMI</th><th className="text-left p-2.5">Name</th><th className="text-left p-2.5">Created</th><th className="text-right p-2.5">Snapshots</th><th className="text-left p-2.5">Status</th></tr>
            </thead>
            <tbody>
              {[...data.amis].sort((a, b) => a.name.localeCompare(b.name) || b.created.localeCompare(a.created)).map((a) => (
                <tr key={a.region + a.imageId} className="border-t border-slate-100">
                  <td className="p-2.5 font-mono text-xs">{a.imageId}<div className="text-[11px] text-slate-400 font-sans">{a.region}</div></td>
                  <td className="p-2.5 text-xs">{a.name}</td>
                  <td className="p-2.5 text-xs">{a.created}</td>
                  <td className="p-2.5 text-xs text-right">{a.snapshotCount} · {a.sizeGiB} GiB</td>
                  <td className="p-2.5"><Badge cls={AMI_ST[a.status]}>{a.status === 'IN_USE' ? 'In use' : a.status === 'KEEP_LATEST' ? 'Latest of group' : 'Deregister candidate'}</Badge></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {view === 'policies' && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
              <tr><th className="text-left p-2.5">Policy</th><th className="text-left p-2.5">State</th><th className="text-left p-2.5">Target</th><th className="text-left p-2.5">Schedule</th><th className="text-left p-2.5">Cross-region copy</th></tr>
            </thead>
            <tbody>
              {data.policies.map((p) => (
                <tr key={p.region + p.policyId} className="border-t border-slate-100 align-top">
                  <td className="p-2.5"><div className="text-xs font-medium text-slate-800">{p.description}</div><div className="font-mono text-[11px] text-slate-400">{p.policyId} · {p.region}</div></td>
                  <td className="p-2.5"><Badge cls={p.state === 'ENABLED' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-100 text-slate-500 border-slate-200'}>{p.state}</Badge></td>
                  <td className="p-2.5 text-xs">{(p.resourceTypes || []).join(', ')} · {(p.targetTags || []).map((t) => `${t.key}:${t.value}`).join(', ')}</td>
                  <td className="p-2.5 text-xs">{(p.schedulesText || []).map((s) => <div key={s.name}>{s.frequency} · {s.retain}</div>)}</td>
                  <td className="p-2.5 text-xs">{(p.schedulesText || []).some((s) => s.crossRegion.length) ? p.schedulesText.map((s) => s.crossRegion.join(', ')).join(' ') : <span className="text-slate-400">none</span>}</td>
                </tr>
              ))}
              {data.policies.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-sm text-slate-400">No lifecycle policies found{(data.dlmErrors || []).length ? ' (DLM not readable)' : ''}.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {(data.errors || []).length > 0 && (
        <div className="text-[11px] text-slate-400">Warnings: {data.errors.join(' · ')}</div>
      )}
    </div>
  );
}
